import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Collapse, Dialog, IconButton, LinearProgress, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { toast } from 'react-toastify';
import DosenTooltip from '../../../../../../../../components/ui/DosenTooltip';
import { Bone } from '../../../../../../../../components/ui/DashboardSkeletons';
import {
  API,
  CourseDiff,
  CoursePublishDecisions,
  CoursePublishJob,
  CoursePublishPreview
} from '../../../../../../../../api';
import { isVersioningOffError } from '../../../../../../../../utils/courseVersioningSession';
import { CtaButton } from '../components/SettingsUI';
import { plural } from './diff';
import { useCourseVersioning } from './CourseVersioningContext';
import { diffLessonParts, ExampleLearner, exampleWhatsNew, partLabel } from './lessonParts';
import WhatsNewMessage from '../../../../../../../../components/course-versioning/WhatsNewMessage';

/**
 * Publish Update (PRD A6) over POST publish/preview: Updates (the server diff
 * as one row per changed lesson with its edits), one of three mutually
 * exclusive presets (pre-selected from `suggestedPreset`; the preset's
 * `decisionsDefault` are the decisions sent), "What Will Happen" (the
 * notification with a read-only example What's New), then POST publish + the job
 * polled every 2 s. The preview is fetched once on open (no body = the
 * suggested preset) and re-fetched, debounced, whenever the preset changes,
 * so the learner counts always describe exactly what Publish will send.
 * Styled to match the Learning Tier modal (MemberDevelopmentTierModal) and
 * DosenTooltip.
 */

// ---- Tokens (Learning Tier modal) ----
const BRANDY = '#F79420';
const TEXT = '#FDFDFD';
const TEXT_2 = '#B6B6B6';
const TEXT_3 = '#868686';
const LINE = '#2A2A2A';
const SURFACE = '#161616';
const FONT = 'Inter, sans-serif';
type BadgeId = 'new' | 'updated' | 'action';

const subHeader = {
  width: '100%',
  px: '16px',
  py: '14px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left' as const,
  fontFamily: FONT,
  '&:hover': { background: 'rgba(255, 255, 255, 0.02)' }
};

const MINOR_BLOCKED_REASON =
  'Lessons were added or removed — learners shouldn’t gain or lose a lesson without being told.';
// Even line breaks, so no lone word spills onto its own line.
const BALANCED_WRAP: Record<string, string> = { textWrap: 'balance' };

type PresetId = 'minor' | 'no_action' | 'review_required';
type LineKey = 'newItems' | 'removedItems' | 'textChanged' | 'quizChanged' | 'scenarioChanged';

// Each description answers the same two questions — are learners told, and do
// they have to do anything — so the three read as mutually exclusive.
const PRESETS: {
  id: PresetId;
  title: string;
  desc: string;
  update: boolean;
  action: boolean;
  updateTip: string;
  actionTip: string;
}[] = [
  {
    id: 'minor',
    title: 'Minor Fix',
    desc: 'Small corrections only. Learners aren’t told and nothing is asked of them.',
    update: false,
    action: false,
    updateTip: 'No notice or badges. Learners simply see the corrected content the next time they open the lesson.',
    actionTip: 'Nobody’s progress, scores or completion change.'
  },
  {
    id: 'no_action',
    title: 'Update with no action needed',
    desc: 'Learners are told what’s new, but nobody has to redo completed work.',
    update: true,
    action: false,
    updateTip: 'Learners get an in-app What’s New notice, and changed lessons are badged New or Updated.',
    actionTip:
      'Completed lessons stay complete. New lessons are required only for learners still in progress — optional for those who finished.'
  },
  {
    id: 'review_required',
    title: 'Update that needs a review',
    desc: 'Learners are told what’s new and must complete the changed lessons again.',
    update: true,
    action: true,
    updateTip: 'Learners get an in-app What’s New notice listing exactly what they need to do.',
    actionTip:
      'Changed and new lessons reopen as required for everyone, including learners who already completed them. Unchanged lessons stay complete.'
  }
];

/** What one option means for learners: what they see, what they must do, and which badge appears. */
interface Outcome {
  update: string;
  action: string | null;
  badges: BadgeId[];
}

/** One kind of change and how the chosen preset treats learners for it (the post-publish summary). */
interface Line {
  key: LineKey;
  title: string;
  /** Lessons this line covers — the badges its outcome puts on lessons land on these. */
  ids: string[];
  outcomes: Record<string, Outcome>;
  /** Server effect carrying the learner counts for this line. */
  effectKey: string;
}

const unique = (ids: string[]): string[] => Array.from(new Set(ids));
const learners = (n: number) => plural(n, 'learner', 'learners');

/** Learner counts for a line: the server's for the current choices (latest preview), else totals. */
interface Counts {
  inProgress: number;
  completers: number;
  total: number;
  effect: (key: string, field: string) => number | null;
}

function buildLines(diff: CourseDiff, keys: Set<string>, c: Counts): Line[] {
  const lines: Line[] = [];
  const n = (key: string, field: string, fallback: string): string => {
    const v = c.effect(key, field);
    return v === null ? fallback : learners(v);
  };

  if (keys.has('newItems') && diff.lessons.added.length) {
    const shown = 'Shown to everyone who hasn’t done it yet.';
    lines.push({
      key: 'newItems',
      title: 'New lessons',
      ids: diff.lessons.added.map((l) => l.id),
      effectKey: 'new_lessons',
      outcomes: {
        required_in_progress_optional_completers: {
          update: shown,
          action: `Required for ${n('new_lessons', 'required', `${c.inProgress} in-progress learners`)}${
            c.effect('new_lessons', 'required') === null ? '' : ' in progress'
          }. Optional for ${n('new_lessons', 'optional', `${c.completers} completers`)}${
            c.effect('new_lessons', 'optional') === null ? '' : ' who already completed'
          }.`,
          badges: ['new']
        },
        required_all: {
          update: shown,
          action: `Required for everyone. ${c.completers} completers move back to In progress.`,
          badges: ['new', 'action']
        },
        optional_all: { update: 'Shown as an optional bonus lesson.', action: null, badges: ['new'] }
      }
    });
  }
  if (keys.has('removedItems') && diff.lessons.removed.length) {
    const update = 'No longer shown. Learners’ records on it are archived.';
    lines.push({
      key: 'removedItems',
      title: 'Removed lessons',
      ids: diff.lessons.removed.map((l) => l.id),
      effectKey: 'removed_lessons',
      outcomes: {
        mark_complete: {
          update: `${update} Anyone who has now finished everything else is marked complete${
            c.effect('removed_lessons', 'markedComplete') === null
              ? ''
              : ` (${learners(c.effect('removed_lessons', 'markedComplete') as number)})`
          }.`,
          action: null,
          badges: []
        },
        keep_in_progress: { update: `${update} Learners stay In progress.`, action: null, badges: [] }
      }
    });
  }
  // Reading content: reworded text plus case study and feedback question changes (the policy treats all as content).
  const contentIds = unique([
    ...diff.lessons.textChanged.map((l) => l.id),
    ...diff.sections.filter((s) => s.caseStudies).map((s) => s.lessonId),
    ...(diff.feedback || []).map((f) => f.lessonId)
  ]);
  if (keys.has('textChanged') && contentIds.length) {
    lines.push({
      key: 'textChanged',
      title: 'Lesson Body changes',
      ids: contentIds,
      effectKey: 'text_changed',
      outcomes: {
        keep_silent: { update: 'New wording shows next time it’s opened.', action: null, badges: [] },
        keep_badge: { update: 'Completed lessons stay complete.', action: null, badges: ['updated'] },
        reread: {
          update: 'Completed lessons reopen.',
          action: `Must re-read: reopened for ${n('text_changed', 'reread', 'everyone who completed it')}.`,
          badges: ['updated', 'action']
        }
      }
    });
  }
  const quizIds = unique([
    ...diff.quiz.map((q) => q.lessonId),
    ...diff.sections.filter((s) => s.quiz === 'enabled').map((s) => s.lessonId)
  ]);
  if (keys.has('quizChanged') && quizIds.length) {
    const kept = 'Previous results kept, labeled “Earlier Version”.';
    lines.push({
      key: 'quizChanged',
      title: 'Quiz changes',
      ids: quizIds,
      effectKey: 'quiz_changed',
      outcomes: {
        keep: { update: kept, action: null, badges: [] },
        optional_retake: {
          update: `${kept} Retake offered.`,
          action: `Optional retake for ${n('quiz_changed', 'optional', 'learners with results')}.`,
          badges: ['updated']
        },
        require: {
          update: kept,
          action: `${n('quiz_changed', 'required', 'Learners with results')} must retake the new quiz.`,
          badges: ['updated', 'action']
        }
      }
    });
  }
  const scenarioIds = unique(diff.sections.filter((s) => s.simulation).map((s) => s.lessonId));
  if (keys.has('scenarioChanged') && scenarioIds.length) {
    lines.push({
      key: 'scenarioChanged',
      title: 'Roleplay changes',
      ids: scenarioIds,
      effectKey: 'scenario_changed',
      outcomes: {
        keep: { update: 'Previous results kept.', action: null, badges: [] },
        optional_retake: {
          update: 'Previous results kept. Re-attempt offered.',
          action: `Optional re-attempt for ${n('scenario_changed', 'optional', 'learners with results')}.`,
          badges: ['updated']
        },
        require: {
          update: 'Previous results kept.',
          action: `${n('scenario_changed', 'required', 'Learners with results')} must re-attempt.`,
          badges: ['updated', 'action']
        }
      }
    });
  }
  return lines;
}

/** A row of the Updates list: one lesson (or module) and every edit made to it. */
interface ChangeRow {
  id: string;
  kind: 'lesson' | 'module';
  title: string;
  edits: string[];
}

/**
 * Updates as "Lesson title: Content updated · Quiz Question added", one row per
 * lesson in course order (the draft's), then lessons no longer in the draft
 * (removed), then module-level changes.
 */
function buildChangeRows(diff: CourseDiff, lessonTitles: Map<string, string>): ChangeRow[] {
  const edits = new Map<string, string[]>();
  const diffTitles = new Map<string, string>();
  const add = (id: string, edit: string, title?: string) => {
    const list = edits.get(id) || [];
    list.push(edit);
    edits.set(id, list);
    if (title && !diffTitles.has(id)) diffTitles.set(id, title);
  };

  diff.lessons.added.forEach((l) => add(l.id, 'Lesson added', l.title));
  diff.lessons.removed.forEach((l) => add(l.id, 'Lesson removed', l.title));
  diff.lessons.renamed.forEach((r) => add(r.id, `Renamed from “${r.from}”`, r.to));
  diff.lessons.moved.forEach((l) => add(l.id, 'Moved', l.title));
  diffLessonParts(diff).forEach((parts, id) => parts.forEach((p) => add(id, partLabel(p))));

  const inDraft = Array.from(lessonTitles.keys()).filter((id) => edits.has(id));
  const notInDraft = Array.from(edits.keys()).filter((id) => !lessonTitles.has(id));
  const rows: ChangeRow[] = [...inDraft, ...notInDraft].map((id) => ({
    id,
    kind: 'lesson',
    title: lessonTitles.get(id) || diffTitles.get(id) || 'Untitled lesson',
    edits: edits.get(id) || []
  }));

  const modules = new Map<string, ChangeRow>();
  const addModule = (id: string, title: string, edit: string) => {
    const row = modules.get(id) || { id: `module-${id}`, kind: 'module' as const, title, edits: [] };
    row.edits.push(edit);
    modules.set(id, row);
  };
  diff.modules.added.forEach((m) => addModule(m.id, m.title, 'Module added'));
  diff.modules.removed.forEach((m) => addModule(m.id, m.title, 'Module removed'));
  diff.modules.renamed.forEach((m) => addModule(m.id, m.to, `Module renamed from “${m.from}”`));
  diff.modules.moved.forEach((m) => addModule(m.id, m.title, 'Module reordered'));
  return [...rows, ...Array.from(modules.values())];
}

const panel = { border: `1px solid ${LINE}`, borderRadius: '8px', background: SURFACE, overflow: 'hidden' };
const rowDivider = (i: number) => (i ? `1px solid ${LINE}` : 'none');

const EXAMPLE_LEARNERS: { id: ExampleLearner; label: string }[] = [
  { id: 'inProgress', label: 'Learner in progress' },
  { id: 'completed', label: 'Learner who completed' }
];

function InfoTip({ title, label }: { title: string; label: string }) {
  return (
    <DosenTooltip title={title} placement="bottom">
      <InfoOutlinedIcon
        tabIndex={0}
        aria-label={`${label}: ${title}`}
        onClick={(e) => e.stopPropagation()}
        sx={{ fontSize: 15, color: '#B6B6B6', cursor: 'default', flexShrink: 0 }}
      />
    </DosenTooltip>
  );
}

function Section({
  title,
  hint,
  right,
  children
}: {
  title: string;
  hint?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: TEXT }}>{title}</Typography>
          {hint && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_3, mt: '2px' }}>{hint}</Typography>}
        </Box>
        {right}
      </Box>
      {children}
    </Box>
  );
}

const JOB_POLL_MS = 2_000;
const PREVIEW_DEBOUNCE_MS = 300;
const isTerminal = (status?: string) => status === 'completed' || status === 'partial' || status === 'failed';

/** Canonical key of a preset + decisions pair, so identical choices never re-fetch the preview. */
const choicesKey = (preset: string, decisions?: CoursePublishDecisions | null): string => {
  const d = decisions || {};
  return `${preset}|${(Object.keys(d) as (keyof CoursePublishDecisions)[])
    .sort()
    .map((k) => `${k}=${d[k]}`)
    .join(',')}`;
};

export function PublishUpdateModal({ programName }: { programName: string }) {
  const v = useCourseVersioning();
  const [phase, setPhase] = useState<'loading' | 'form' | 'publishing' | 'done' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');
  const [preview, setPreview] = useState<CoursePublishPreview | null>(null);
  const [preset, setPreset] = useState<PresetId>('no_action');
  const [notificationsOpen, setNotificationsOpen] = useState(true);
  const [exampleLearner, setExampleLearner] = useState<ExampleLearner>('inProgress');
  const [summary, setSummary] = useState<{ title: string; text: string }[]>([]);
  const [publishedVersion, setPublishedVersion] = useState<number | null>(null);
  const [job, setJob] = useState<CoursePublishJob | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [pollingSince, setPollingSince] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const openedAtRef = useRef(0);
  // Re-preview for the current choices: in flight, failed, manual retry.
  const [previewPending, setPreviewPending] = useState(false);
  const [previewStale, setPreviewStale] = useState(false);
  const [previewRetry, setPreviewRetry] = useState(0);
  /** Choices the current `preview` was computed for (see choicesKey). */
  const previewKeyRef = useRef<string | null>(null);
  /** Latest preview request; responses to older ones are dropped. */
  const previewSeqRef = useRef(0);

  const open = Boolean(v?.publishOpen);
  const grpId = v?.grpId || '';
  const lessonTitles = v?.lessonTitles;

  const loadPreview = useCallback(async () => {
    if (!grpId) return;
    const stamp = Date.now();
    openedAtRef.current = stamp;
    setPhase('loading');
    setLoadError('');
    try {
      // No body: the server previews its suggested preset and echoes it back.
      const res = await API.getPublishPreview(grpId);
      const data = res?.data?.data;
      if (openedAtRef.current !== stamp) return;
      if (!data) throw new Error('Empty preview');
      previewSeqRef.current += 1;
      previewKeyRef.current = choicesKey(data.preset ?? data.suggestedPreset, data.decisions);
      setPreviewPending(false);
      setPreviewStale(false);
      setPreview(data);
      setPreset(data.suggestedPreset);
      setNotificationsOpen(true);
      setExampleLearner('inProgress');
      setPhase('form');
    } catch (err: any) {
      if (openedAtRef.current !== stamp) return;
      if (isVersioningOffError(err)) {
        v?.setPublishOpen(false);
        return;
      }
      setLoadError(err?.response?.data?.message || 'Could not prepare this update. Please try again.');
      setPhase('error');
    }
  }, [grpId, v]);

  // Fresh preview each time it opens, pre-selected from the suggested preset.
  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setJob(null);
    setJobId(null);
    setPublishedVersion(null);
    setSummary([]);
    setPublishing(false);
    setPollingSince(null);
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Job polling every 2 s until completed | partial | failed.
  useEffect(() => {
    if (!open || !jobId || phase !== 'publishing') return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await API.getPublishJob(grpId, jobId);
        const data = res?.data?.data;
        if (cancelled || !data) return;
        setJob(data);
        if (isTerminal(data.status)) {
          cancelled = true;
          clearInterval(interval);
          if (publishedVersion !== null) await v?.afterPublish(publishedVersion);
          toast.success(`Published v${publishedVersion ?? data.toVersion}`);
          setPhase('done');
        }
      } catch (err) {
        if (isVersioningOffError(err)) {
          cancelled = true;
          clearInterval(interval);
        }
        /* transient: keep polling */
      }
    };
    const interval = setInterval(tick, JOB_POLL_MS);
    const ticker = setInterval(() => setNow(Date.now()), 5_000);
    tick();
    return () => {
      cancelled = true;
      clearInterval(interval);
      clearInterval(ticker);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId, phase, grpId]);

  const diff = preview?.diff || null;
  const decisionKeys = useMemo(
    () => new Set(Object.keys(preview?.decisionsDefault?.no_action || {})),
    [preview?.decisionsDefault]
  );
  // The preset is the whole choice: its server defaults are the decisions sent.
  const decisions: CoursePublishDecisions = useMemo(
    () => preview?.decisionsDefault?.[preset] || {},
    [preview?.decisionsDefault, preset]
  );

  // Re-preview for the chosen preset (debounced): `effects` and the learner
  // counts always describe `preset` + `decisions`, the exact payload Publish
  // sends. Stale responses are dropped; the previous counts stay on screen
  // until the new ones arrive. A preset the current preview was computed for
  // never re-fetches.
  useEffect(() => {
    if (!open || phase !== 'form' || !preview || !grpId) return undefined;
    const key = choicesKey(preset, decisions);
    if (key === previewKeyRef.current) {
      // Back on the previewed choices: drop any in-flight request for others.
      previewSeqRef.current += 1;
      setPreviewPending(false);
      return undefined;
    }
    setPreviewPending(true);
    const seq = ++previewSeqRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const res = await API.getPublishPreview(grpId, { preset, decisions });
        if (seq !== previewSeqRef.current) return;
        const data = res?.data?.data;
        if (!data) throw new Error('Empty preview');
        previewKeyRef.current = key;
        setPreview(data);
        setPreviewStale(false);
        setPreviewPending(false);
      } catch (err) {
        if (seq !== previewSeqRef.current) return;
        if (isVersioningOffError(err)) {
          v?.setPublishOpen(false);
          return;
        }
        setPreviewStale(true);
        setPreviewPending(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, grpId, preset, decisions, previewRetry]);

  // Learner counts from the latest preview, i.e. for the current choices.
  const effectCount = useCallback(
    (key: string, field: string): number | null => {
      const e = preview?.effects.find((x) => x.key === key);
      const n = e?.learnerCounts?.[field];
      return typeof n === 'number' ? n : null;
    },
    [preview]
  );

  const L = preview?.learnerCounts || { total: 0, inProgress: 0, completers: 0, notStarted: 0 };
  const lines = useMemo(
    () =>
      diff
        ? buildLines(diff, decisionKeys, {
            inProgress: L.inProgress,
            completers: L.completers,
            total: L.total,
            effect: effectCount
          })
        : [],
    [diff, decisionKeys, L.inProgress, L.completers, L.total, effectCount]
  );
  const changeRows = useMemo(
    () => (diff ? buildChangeRows(diff, lessonTitles || new Map()) : []),
    [diff, lessonTitles]
  );

  if (!v) return null;

  const fromV = preview?.fromVersion ?? v.liveVersion;
  const toV = preview?.toVersion ?? v.liveVersion + 1;
  const eff = (key: LineKey) => decisions[key] as string | undefined;
  // Notifying follows "Update Learners" on the chosen type — not a separate choice.
  const notify = (decisions.notify ?? (preset === 'minor' ? 'none' : 'in_app')) === 'in_app';
  // Lessons appearing or disappearing is never a "minor fix".
  const minorAllowed = Boolean(diff && !diff.lessons.added.length && !diff.lessons.removed.length);
  const notifiedCount = effectCount('notify', 'notified') ?? L.total;
  const noChanges = Boolean(diff && lines.length === 0 && changeRows.length === 0);

  const canCloseWhilePublishing = pollingSince !== null && now - pollingSince > 30_000;
  const close = () => {
    if (phase === 'publishing' && !canCloseWhilePublishing) return;
    v.setPublishOpen(false);
  };

  // What a learner would read, for the read-only example under Notifications.
  // A group with nobody in it is locked; if it's the chosen one, show the other.
  const exampleLearnerCount: Record<ExampleLearner, number> = { inProgress: L.inProgress, completed: L.completers };
  const otherLearner: ExampleLearner = exampleLearner === 'inProgress' ? 'completed' : 'inProgress';
  const shownLearner: ExampleLearner =
    exampleLearnerCount[exampleLearner] === 0 && exampleLearnerCount[otherLearner] > 0 ? otherLearner : exampleLearner;
  const example = diff ? exampleWhatsNew(diff, decisions, shownLearner, lessonTitles || new Map()) : null;

  const handlePublish = async () => {
    if (!preview || publishing) return;
    const rows = lines.map((l) => {
      const cur = eff(l.key);
      const o = cur ? l.outcomes[cur] : undefined;
      return { title: l.title, text: o ? (o.action ? `${o.update} ${o.action}` : o.update) : '' };
    });
    rows.push({
      title: 'Learner Notifications',
      text: notify ? `${learners(notifiedCount)} get an in-app What’s New notice.` : 'No learners were notified.'
    });
    setSummary(rows);
    setPublishing(true);
    try {
      // No `whatsNew`: each learner's notice is written from the publish itself,
      // never from admin-edited text.
      const res = await API.publishCourse(grpId, { preset, decisions });
      const data = res?.data?.data;
      if (!data) throw new Error('Empty publish response');
      setPublishedVersion(data.versionNumber);
      setJob({
        jobId: data.jobId,
        grpId,
        status: 'processing',
        fromVersion: preview.fromVersion,
        toVersion: data.versionNumber,
        preset,
        decisions,
        whatsNew: null,
        totalLearners: data.totalLearners,
        processedLearners: 0,
        failedLearners: 0
      });
      setJobId(data.jobId);
      setPollingSince(Date.now());
      setPhase('publishing');
    } catch (err: any) {
      if (isVersioningOffError(err)) {
        v.setPublishOpen(false);
        return;
      }
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const body = err?.response?.data;
      if (status === 400 && code === 'NO_CHANGES') {
        toast.info(`Nothing to publish. Your draft matches v${body?.liveVersion ?? fromV}.`);
        v.setPublishOpen(false);
        v.reloadDraft();
      } else if (status === 409 && code === 'BASE_VERSION') {
        toast.warn(
          `Someone published v${
            body?.liveVersion ?? '?'
          } while you were editing. Your draft was reloaded. Review it and publish again.`
        );
        v.setPublishOpen(false);
        v.reloadDraft();
      } else if (status === 409 && code === 'EDIT_LOCK') {
        toast.warn(
          `${body?.lock?.name || 'Another admin'} is editing this program, so publishing is unavailable right now.`
        );
        v.noteLockHolder(body?.lock || null);
        v.setPublishOpen(false);
      } else if (status === 409 && code === 'NOT_PUBLISHABLE') {
        // A Scored Roleplay on but never written, or a capability left with no lesson
        // (docs/PRD-capability-lesson-roleplays.md R6, R8). Nothing was changed for learners.
        toast.error(`This update can't be published yet. ${body?.message || ''}. Fix it in the draft, then publish again.`);
      } else {
        toast.error(body?.message || 'The update could not be published. Nothing was changed for learners. Try again.');
      }
    } finally {
      setPublishing(false);
    }
  };

  const jobStatus = job?.status;
  const progressValue =
    jobStatus && isTerminal(jobStatus)
      ? 100
      : job && job.totalLearners > 0
      ? Math.min(99, Math.round((job.processedLearners / job.totalLearners) * 100))
      : 5;
  // 0 = publish request in flight, 1 = learner records updating, 3 = all done.
  const step = !job ? 0 : isTerminal(jobStatus) ? 3 : 1;
  const steps = [
    `Publishing course v${publishedVersion ?? toV}`,
    `Updating ${job ? learners(job.totalLearners) : 'learners'}’ records${
      job && job.totalLearners > 0 ? ` · ${job.processedLearners}/${job.totalLearners}` : ''
    }`,
    notify ? 'Sending What’s New' : 'Finishing up'
  ];

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth={false}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.60)', backdropFilter: 'blur(10.95px)' } }
      }}
      PaperProps={{
        sx: {
          width: 960,
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#0F0F0F',
          color: TEXT,
          borderRadius: '8px',
          outline: '1px solid #444444',
          outlineOffset: '-1px',
          overflow: 'hidden',
          boxShadow: 'none'
        }
      }}
    >
      {/* Header — same treatment as the Learning Tier modal */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'baseline',
          gap: '12px',
          pl: '25px',
          pr: '56px',
          py: '20px',
          borderBottom: `1px solid ${LINE}`,
          position: 'relative'
        }}
      >
        <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, color: TEXT }}>Publish update</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_3 }}>
          {programName || 'Program'} · v{fromV} → v{publishedVersion ?? toV}
        </Typography>
        {(phase !== 'publishing' || canCloseWhilePublishing) && (
          <IconButton
            aria-label="Close"
            onClick={close}
            sx={{ p: 0.5, position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)' }}
          >
            <CloseIcon sx={{ fontSize: 20, color: TEXT }} />
          </IconButton>
        )}
      </Box>

      {phase === 'loading' && (
        <Box
          sx={{ px: '25px', py: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}
        >
          <Box
            sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}
            aria-busy="true"
            aria-label={`Comparing your draft with v${fromV}`}
          >
            <Bone width={120} height={16} />
            <Bone height={88} radius="8px" />
            <Bone width={220} height={16} sx={{ mt: '12px' }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px' }}>
              <Bone height={132} radius="8px" />
              <Bone height={132} radius="8px" />
              <Bone height={132} radius="8px" />
            </Box>
          </Box>
        </Box>
      )}

      {phase === 'error' && (
        <Box sx={{ px: '25px', py: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#F1808A' }}>{loadError}</Typography>
          <Box sx={{ display: 'flex', gap: '12px' }}>
            <Box sx={{ width: 120 }}>
              <CtaButton onClick={loadPreview}>Try Again</CtaButton>
            </Box>
          </Box>
        </Box>
      )}

      {phase === 'form' && diff && (
        <>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              px: '25px',
              pt: '24px',
              pb: '28px',
              display: 'flex',
              flexDirection: 'column',
              gap: '32px'
            }}
          >
            {/* 1 · Updates — each changed lesson and its edits, then how each kind of change is handled */}
            <Section title="Updates" hint={`Changes in your draft since v${fromV}.`}>
              <Box sx={panel}>
                {changeRows.length ? (
                  changeRows.map((r, i) => (
                    <Box
                      key={r.id}
                      sx={{
                        px: '16px',
                        py: '10px',
                        borderTop: rowDivider(i),
                        display: 'grid',
                        gridTemplateColumns: '240px 1fr',
                        gap: '16px'
                      }}
                    >
                      <Typography
                        sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: TEXT, lineHeight: '20px' }}
                      >
                        {r.title}:
                      </Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_2, lineHeight: '20px' }}>
                        {r.edits.join(' · ')}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ px: '16px', py: '12px', fontFamily: FONT, fontSize: 13, color: TEXT_2 }}>
                    No differences found between your draft and v{fromV}.
                  </Typography>
                )}
              </Box>
            </Section>

            {/* 2 · Update type */}
            <Section title="What kind of update is this?">
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px', pt: '6px' }}>
                {PRESETS.map((p) => {
                  const selected = preset === p.id;
                  // Adding or removing lessons is never a minor fix — learners
                  // would silently gain or lose a lesson with no notice.
                  const disabled = p.id === 'minor' && !minorAllowed;
                  return (
                    <Box
                      key={p.id}
                      component="button"
                      type="button"
                      aria-pressed={selected}
                      disabled={disabled}
                      title={disabled ? MINOR_BLOCKED_REASON : undefined}
                      onClick={() => setPreset(p.id)}
                      sx={{
                        position: 'relative',
                        p: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        textAlign: 'left',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.45 : 1,
                        borderRadius: '8px',
                        border: `1px solid ${selected ? BRANDY : LINE}`,
                        boxShadow: selected ? `inset 0 0 0 1px ${BRANDY}` : 'none',
                        background: selected ? 'rgba(247, 148, 32, 0.06)' : SURFACE,
                        color: TEXT,
                        fontFamily: FONT,
                        transition: 'border-color 120ms',
                        '&:hover': { borderColor: selected || disabled ? undefined : '#444444' }
                      }}
                    >
                      {disabled && (
                        <Box
                          component="span"
                          sx={{
                            position: 'absolute',
                            top: -10,
                            right: 12,
                            fontFamily: FONT,
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: '18px',
                            px: '8px',
                            borderRadius: '999px',
                            border: `1px solid ${LINE}`,
                            background: '#1A1A1A',
                            color: TEXT_3
                          }}
                        >
                          Not available
                        </Box>
                      )}
                      {p.id === preview?.suggestedPreset && !disabled && (
                        <Box
                          component="span"
                          sx={{
                            position: 'absolute',
                            top: -10,
                            right: 12,
                            fontFamily: FONT,
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: '18px',
                            px: '8px',
                            borderRadius: '999px',
                            border: '1px solid #2E6B52',
                            background: '#12211A',
                            color: '#6FD1A6'
                          }}
                        >
                          Suggested
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            flexShrink: 0,
                            boxSizing: 'border-box',
                            borderRadius: '50%',
                            border: `2px solid ${selected ? BRANDY : TEXT_3}`,
                            boxShadow: selected ? `inset 0 0 0 3px #0F0F0F` : 'none',
                            background: selected ? BRANDY : 'transparent'
                          }}
                        />
                        <Typography
                          sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: TEXT, whiteSpace: 'nowrap' }}
                        >
                          {p.title}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          flex: 1,
                          fontFamily: FONT,
                          fontSize: 12.5,
                          lineHeight: '19px',
                          color: TEXT_2,
                          ...BALANCED_WRAP
                        }}
                      >
                        {disabled ? MINOR_BLOCKED_REASON : p.desc}
                      </Typography>
                      <Box
                        sx={{
                          pt: '10px',
                          borderTop: `1px solid ${LINE}`,
                          display: 'grid',
                          gridTemplateColumns: 'auto auto 1fr',
                          alignItems: 'center',
                          columnGap: '6px',
                          rowGap: '6px',
                          fontFamily: FONT,
                          fontSize: 12,
                          color: TEXT_2
                        }}
                      >
                        {[
                          { label: 'Update learners', value: p.update, tip: p.updateTip },
                          { label: 'Require learner action', value: p.action, tip: p.actionTip }
                        ].map((row) => (
                          <React.Fragment key={row.label}>
                            <span style={{ whiteSpace: 'nowrap' }}>{row.label}</span>
                            <InfoTip title={row.tip} label={row.label} />
                            <span
                              style={{
                                justifySelf: 'end',
                                fontWeight: 700,
                                color: row.value ? TEXT : TEXT_3
                              }}
                            >
                              {row.value ? 'Yes' : 'No'}
                            </span>
                          </React.Fragment>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Section>

            {/* 3 · What Will Happen — the learner-facing consequences */}
            <Section
              title="What will happen"
              hint="Set by your selection above."
              right={
                previewPending ? (
                  <CircularProgress size={14} thickness={5} sx={{ color: TEXT_3, mb: '2px' }} aria-label="Updating" />
                ) : undefined
              }
            >
              {/* Learner counts come from the server preview for the current choices (spinner while it re-runs). */}
              <Box aria-busy={previewPending} sx={panel}>
                {/* Notifications */}
                <Box>
                  <Box
                    component="button"
                    type="button"
                    aria-expanded={notificationsOpen}
                    onClick={() => setNotificationsOpen((o) => !o)}
                    sx={subHeader}
                  >
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 20,
                        color: TEXT_3,
                        transform: notificationsOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 150ms'
                      }}
                    />
                    <Typography sx={{ flex: 1, fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: TEXT }}>
                      Notifications
                    </Typography>
                    <Typography
                      sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: notify ? BRANDY : TEXT_3 }}
                    >
                      {notify ? `${learners(notifiedCount)} notified` : 'Not announced to learners'}
                    </Typography>
                  </Box>
                  <Collapse in={notificationsOpen} unmountOnExit>
                    <Box sx={{ ml: '46px', mr: '16px', pb: '16px' }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_2, lineHeight: '20px' }}>
                        {notify
                          ? 'Each learner gets a custom message from Elliott that includes only what is relevant to them.'
                          : 'No notice is sent. Minor Fix changes aren’t announced.'}
                      </Typography>
                      {notify && example && (
                        <Box sx={{ mt: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <Typography sx={{ flex: 1, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: TEXT }}>
                              Example notification
                            </Typography>
                            <Box
                              role="group"
                              aria-label="Example learner"
                              sx={{
                                display: 'inline-flex',
                                border: `1px solid ${LINE}`,
                                borderRadius: '6px',
                                overflow: 'hidden'
                              }}
                            >
                              {EXAMPLE_LEARNERS.map((o, i) => {
                                const sel = shownLearner === o.id;
                                // Nobody in this group gets the notice, so there's nothing to show for it.
                                const locked = exampleLearnerCount[o.id] === 0;
                                return (
                                  <Box
                                    key={o.id}
                                    component="button"
                                    type="button"
                                    aria-pressed={sel}
                                    disabled={locked}
                                    title={
                                      locked
                                        ? `No ${
                                            o.id === 'inProgress' ? 'learners in progress' : 'learners have completed'
                                          }`
                                        : undefined
                                    }
                                    onClick={() => setExampleLearner(o.id)}
                                    sx={{
                                      px: '10px',
                                      py: '4px',
                                      border: 0,
                                      borderLeft: i ? `1px solid ${LINE}` : 0,
                                      cursor: locked ? 'not-allowed' : 'pointer',
                                      opacity: locked ? 0.45 : 1,
                                      fontFamily: FONT,
                                      fontSize: 12,
                                      fontWeight: sel ? 700 : 400,
                                      background: sel ? '#444444' : '#2A2A2A',
                                      color: sel ? TEXT : TEXT_2
                                    }}
                                  >
                                    {o.label}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                          <WhatsNewMessage whatsNew={example} fontSize={13} color={TEXT} mutedColor={TEXT_2} quoted />
                          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_3, lineHeight: '18px' }}>
                            {`An in-app notice goes to ${learners(notifiedCount)} (${L.completers} completed · ${
                              L.inProgress
                            } in progress). ${
                              L.notStarted
                            } learners who have not started simply see the updated course when they begin.`}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              </Box>
              {previewStale && !previewPending && (
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#F1808A' }}>
                  These lines are for your previous choices. The update for the latest ones failed.{' '}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setPreviewRetry((n) => n + 1)}
                    sx={{
                      p: 0,
                      border: 0,
                      background: 'none',
                      cursor: 'pointer',
                      color: BRANDY,
                      fontFamily: FONT,
                      fontSize: 'inherit',
                      fontWeight: 600
                    }}
                  >
                    Try again
                  </Box>
                </Typography>
              )}
            </Section>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              flexShrink: 0,
              px: '25px',
              py: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              borderTop: `1px solid ${LINE}`
            }}
          >
            <Typography sx={{ flex: 1, fontFamily: FONT, fontSize: 12.5, color: TEXT_3 }}>
              Learners are updated in the background right after you publish.
            </Typography>
            <Button
              onClick={close}
              sx={{
                height: 38,
                px: '18px',
                textTransform: 'none',
                fontFamily: FONT,
                fontWeight: 600,
                fontSize: 13,
                color: TEXT,
                border: '1px solid #444444',
                borderRadius: '6px'
              }}
            >
              Cancel
            </Button>
            <Box sx={{ width: 132 }}>
              <CtaButton onClick={handlePublish} disabled={publishing || noChanges || v.readOnly}>
                {publishing ? 'Publishing...' : 'Publish Update'}
              </CtaButton>
            </Box>
          </Box>
        </>
      )}

      {phase === 'publishing' && (
        <Box sx={{ px: '25px', py: '32px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{ height: 6, borderRadius: 3, background: LINE, '& .MuiLinearProgress-bar': { background: BRANDY } }}
          />
          {steps.map((s, i) => (
            <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  boxSizing: 'border-box',
                  border: `2px solid ${step >= i ? BRANDY : '#444444'}`,
                  background: step > i ? BRANDY : 'transparent'
                }}
              />
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: 14,
                  fontWeight: step === i ? 700 : 500,
                  color: step >= i ? TEXT : TEXT_3
                }}
              >
                {s}
              </Typography>
            </Box>
          ))}
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_3 }}>
            v{publishedVersion ?? toV} is already live for learners. Their records are being updated in the background
            {canCloseWhilePublishing ? '. You can close this window; the update carries on.' : '.'}
          </Typography>
        </Box>
      )}

      {phase === 'done' && (
        <Box sx={{ px: '25px', py: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: jobStatus === 'failed' ? 'rgba(241, 128, 138, 0.16)' : 'rgba(76, 175, 132, 0.16)',
                color: jobStatus === 'failed' ? '#F1808A' : '#6FD1A6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {jobStatus === 'failed' ? (
                <InfoOutlinedIcon sx={{ fontSize: 20 }} />
              ) : (
                <CheckIcon sx={{ fontSize: 20 }} />
              )}
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, color: TEXT }}>
              v{publishedVersion} is live
            </Typography>
          </Box>
          <Box sx={panel}>
            {summary.map((s, i) => (
              <Box key={s.title} sx={{ px: '16px', py: '12px', borderTop: rowDivider(i) }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: TEXT }}>{s.title}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_2, lineHeight: '20px', mt: '2px' }}>
                  {s.text}
                </Typography>
              </Box>
            ))}
            <Box sx={{ px: '16px', py: '12px', borderTop: rowDivider(summary.length) }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: TEXT }}>
                Learner records
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_2, lineHeight: '20px', mt: '2px' }}>
                {jobStatus === 'failed'
                  ? `The learner update did not complete${
                      job?.error ? ` (${job.error})` : ''
                    }. The course is live; contact support to re-run the update.`
                  : `${job?.processedLearners ?? 0} of ${learners(job?.totalLearners ?? 0)} updated${
                      job?.failedLearners ? ` · ${job.failedLearners} failed and can be re-run` : ''
                    }.`}
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_3 }}>
            Saved as “Published v{publishedVersion}” in Version History.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ width: 110 }}>
              <CtaButton onClick={() => v.setPublishOpen(false)}>Done</CtaButton>
            </Box>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
